/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { Component, OnInit, Optional, ViewChild } from '@angular/core'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'
import { ConcursoService } from 'src/app/services/concurso.service'
import { ConcursoDetalheStore } from '../concurso-detalhe/concurso-detalhe.store'
import { UnespCoreAuthService, UnespCoreMessageService, UnespCoreUserService } from 'src/libs/unesp-core'
import { ActivatedRoute, Router } from '@angular/router'
import { Concurso, Tema } from 'src/app/models/concurso'
import { inputValitador } from 'src/app/utils/input-valitador'
import { dataPermitida } from 'src/app/utils/date-utils';
import {
  ArquivoUploadComponent,
  ArquivoSelecionado,
  arquivoUploadValidator,
  ArquivoStatus,
} from 'src/app/modules/arquivo-upload/arquivo-upload.component'
import { TipoConcurso, TipoConcursoDTA } from 'src/app/enums/tipo-concurso'
import { Usuario } from 'src/app/models/usuario'
import { COMMA, ENTER, SPACE } from '@angular/cdk/keycodes'
import { MatChipInputEvent, MatChipGrid } from '@angular/material/chips'
import { MatTableDataSource } from '@angular/material/table'
import { IPendingChanges } from 'src/app/utils/pending-changes'
import { Campus } from 'src/app/models/campus'
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../confirm-dialog/confirm-dialog.component'
import { MatDialog } from '@angular/material/dialog'
import { environment } from 'src/environments/environment'
import { firstValueFrom, Subscription } from 'rxjs'
import { unicampEmailFormatValidator } from 'src/app/utils/unicamp-email-format-validator'

@Component({
  selector: 'app-cadastro-concurso',
  templateUrl: './cadastro-concurso.component.html',
  styleUrls: ['./cadastro-concurso.component.css'],
})
export class CadastroConcursoComponent implements OnInit, IPendingChanges {
  readonly chipsSeparatorKeys = [ENTER, COMMA, SPACE] as const
  responsaveis: string[] = []

  @ViewChild('upload') upload: ArquivoUploadComponent | undefined
  @ViewChild('responsaveisChipList') responsaveisChipList: MatChipGrid | undefined

  editalUploadPath: string = 'edital/'
  editalUploadFc: FormControl<ArquivoSelecionado>

  colunasPontoGrid: string[] = ['data', 'titulo', 'action']
  pontosDataSource: MatTableDataSource<Tema> = new MatTableDataSource()
  hasPontos: boolean = false
  isTipoDocente: boolean = true // o padrão é false, mas devio ao card E243510-189 foi alterado para true para sempre ocultar os campos
  pontoInput = new FormControl('')
  editarPontoIdx: number = -1
  alteracoesNosPontos: boolean = false
  dataFimOriginal?: Date
  dataInicioOriginal?: Date

  tipoConcurso = TipoConcurso
  tipoConcursoHabilitado: string[] = ['PT','PTPP']
  tipoConcursoDTA = TipoConcursoDTA
  campusHabilitados: Campus[] = []

  numeroDespachoPattern: string = '(^[0-9]{1,5}\\/\\d{4}$)'
  horaFimConcurso: string = environment.concursosHoraFim
  horaInicioConcurso: string = environment.concursosHoraInicio

  filtroData = dataPermitida;

  get tituloPage(): string {
    if (this.id) {
      const tituloValue = this.titulo.value?.trim();
      return tituloValue ? `Concurso #${this.id}: ${tituloValue}` : `Concurso #${this.id}`;
    }
    return 'Concurso';
  }

  id?: number
  titulo = new FormControl('', Validators.required)
  descricao = new FormControl('', Validators.required)
  tipo = new FormControl('', Validators.required)
  uuid = new FormControl('', Validators.required)
  inicio = new FormControl(new Date(), Validators.required)
  fim = new FormControl(new Date(), Validators.required)
  inicioFase2 = new FormControl(new Date())
  fimFase2 = new FormControl(new Date())
  horaInicio = new FormControl({ value: '', disabled: true }, [
    Validators.required,
    this.horaDentroHorarioFuncionamentoUnicamp,
  ])
  horaFim = new FormControl({ value: '', disabled: true }, [
    Validators.required,
    this.horaDentroHorarioFuncionamentoUnicamp,
  ])
  vagas = new FormControl('')
  valorInscricao = new FormControl('')
  edital = new FormControl('')
  editalSize = new FormControl('')
  salario = new FormControl('')
  destaque = new FormControl('')
  resumo = new FormControl('')
  ativo = new FormControl('')
  online = new FormControl({ value: 'true', disabled: true })
  responsaveisRecurso = new FormControl('', Validators.email)
  andamento = new FormControl('')
  habilitarDevolucao = new FormControl('')
  processo = new FormControl('', Validators.required)
  numeroContad = new FormControl('')
  numeroDespacho = new FormControl('')
  contatoEmail = new FormControl('', Validators.required)
  emailDepartamento = new FormControl('')
  contatoTelefone = new FormControl('')
  permiteProvaIngles = new FormControl('', Validators.required)
  area = new FormControl('')
  departamento = new FormControl('')
  disciplinas = new FormControl('')

  form = new FormGroup({
    titulo: this.titulo,
    descricao: this.descricao,
    tipo: this.tipo,
    uuid: this.uuid,
    inicio: this.inicio,
    fim: this.fim,
    inicioFase2: this.inicioFase2,
    fimFase2: this.fimFase2,
    horaInicio: this.horaInicio,
    horaFim: this.horaFim,
    vagas: this.vagas,
    valorInscricao: this.valorInscricao,
    edital: this.edital,
    editalSize: this.editalSize,
    salario: this.salario,
    destaque: this.destaque,
    resumo: this.resumo,
    ativo: this.ativo,
    online: this.online,
    responsaveisRecurso: this.responsaveisRecurso,
    andamento: this.andamento,
    habilitarDevolucao: this.habilitarDevolucao,
    processo: this.processo,
    numeroContad: this.numeroContad,
    numeroDespacho: this.numeroDespacho,
    contatoEmail: this.contatoEmail,
    emailDepartamento: this.emailDepartamento,
    contatoTelefone: this.contatoTelefone,
    permiteProvaIngles: this.permiteProvaIngles,
    area: this.area,
    departamento: this.departamento,
    disciplinas: this.disciplinas,
  })

  constructor(
    private concursoService: ConcursoService,
    @Optional() private store: ConcursoDetalheStore | null,
    private unespCoreMessageService: UnespCoreMessageService,
    private unespCoreUserService: UnespCoreUserService,
    private route: ActivatedRoute,
    private router: Router,
    private unespCoreAuthService: UnespCoreAuthService,
    private readonly dialog: MatDialog
  ) {
    this.editalUploadFc = new FormControl(new ArquivoSelecionado(), {
      validators: [arquivoUploadValidator(false)],
    }) as FormControl<ArquivoSelecionado>
    this.horaFimConcurso = environment.concursosHoraFim
  }

  ngOnInit(): void {
    this.tipo.valueChanges.subscribe(value => {
      if (value == 'PT' || value == 'PTPP') {
        this.area?.setValidators([Validators.required])
        this.disciplinas?.setValidators([Validators.required])
        this.vagas?.setValidators([Validators.required])
      } else {
        this.area?.clearValidators()
        this.disciplinas?.clearValidators()
        this.vagas?.clearValidators()
      }

      this.area?.updateValueAndValidity()
      this.disciplinas?.updateValueAndValidity()
    })
    this.concursoService.camposHabilitados().subscribe(campus => this.campusHabilitados = campus)
    ;(this.route.parent?.params || this.route.params).subscribe(params => {
      this.id = params['id']
      this.form.reset()
      if (this.id) {
        this.editalUploadPath += this.id
        const source$ = this.store ? this.store.load(Number(this.id)) : this.concursoService.get(this.id)
        source$.subscribe(obj => {
          this.titulo.setValue(obj.titulo)
          if (obj.edital) {
            const arquivoSelecionado = new ArquivoSelecionado()
            arquivoSelecionado.setNameAndSize(obj.edital, obj.editalSize as number)
            arquivoSelecionado.status = ArquivoStatus.ENVIADO
            this.editalUploadFc.setValue(arquivoSelecionado)
          }
          this.editalUploadFc.updateValueAndValidity()
          this.descricao.setValue(obj.descricao)
          this.vagas.setValue(obj.vagas != null ? obj.vagas.toString() : '')
          this.valorInscricao.setValue(this.formatarValor(Number.parseFloat(obj.valorInscricao?.toString())))
          this.inicio.setValue(obj.inicio as any)
          this.fim.setValue(obj.fim as any)
          this.dataFimOriginal = this.parseLocalDate(obj.fim.toString())
          this.dataInicioOriginal = this.parseLocalDate(obj.inicio.toString())
          if (obj.inicioFase2 !== null) {
            this.inicioFase2.setValue(obj.inicioFase2 as any)
          }
          if (obj.fimFase2 !== null) {
            this.fimFase2.setValue(obj.fimFase2 as any)
          }
          this.edital.setValue(obj.edital)
          this.salario.setValue(
            obj.salario == null ? '' : this.formatarValor(Number.parseFloat(obj.salario.toString()))
          )
          this.destaque.setValue(obj.destaque)
          this.resumo.setValue(obj.resumo)
          this.uuid.setValue(obj.uuid.toString())
          this.tipo.setValue(obj.tipo)
          this.andamento.setValue(obj.andamento)
          this.horaInicio.setValue(obj.horaInicio.toString())
          this.horaFim.setValue(obj.horaFim.toString())
          this.habilitarDevolucao.setValue(obj.habilitarDevolucao?.toString())
          this.editalSize.setValue(obj.editalSize != null ? obj.editalSize.toString() : '')
          this.processo.setValue(obj.processo)
          this.numeroContad.setValue(obj.numeroContad)
          this.numeroDespacho.setValue(obj.numeroDespacho)
          this.contatoEmail.setValue(obj.contatoEmail)
          this.emailDepartamento.setValue(obj.emailDepartamento)
          this.contatoTelefone.setValue(obj.contatoTelefone)
          this.permiteProvaIngles.setValue(obj.permiteProvaIngles.toString())
          this.ativo.setValue(obj.ativo.toString())
          this.area.setValue(obj.area)
          this.departamento.setValue(obj.departamento)
          this.disciplinas.setValue(obj.disciplinas)

          obj.responsaveisRecurso.split(',').forEach(res => {
            const value = res.trim()
            if (value) this.responsaveis.push(value)
          })
          if (typeof obj.temas != 'undefined') this.pontosDataSource.data = obj.temas
        })
      } else {
        this.setValoresPadroesCriacaoConcurso()
      }
    })

    //Remove errorState do mat-chip quando a input atende o formato de email
    this.responsaveisRecurso.valueChanges.subscribe(() =>
      !this.responsaveisRecurso.invalid ? (this.responsaveisChipList!.errorState = false) : null
    )

    this.tipo.valueChanges.subscribe(value => {
      this.setHasPontos(value)
      this.setIsTipoDocente(value)
    })

    this.ativo.valueChanges.subscribe(value => {
      if (value === 'true') {
        this.editalUploadFc.setValidators([Validators.required, arquivoUploadValidator(true)])
      } else {
        this.editalUploadFc.clearValidators()
      }
      this.editalUploadFc.updateValueAndValidity()
    })
  }

  setValoresPadroesCriacaoConcurso(): void {
    this.habilitarDevolucao.setValue('false')
    this.ativo.setValue('false')
    this.horaFim.setValue(this.horaFimConcurso)
    this.horaInicio.setValue(this.horaInicioConcurso)
    this.tipo.setValue('PT')
    this.online.setValue('true')
  }

  async selecionaUnidadeAutomaticamente(): Promise<void> {
    const unidadeUsuario: number = (<Usuario>this.unespCoreUserService.getUser()).idUnidadeUniversitaria

    if (!this.campusHabilitados.length) {
      this.campusHabilitados = await firstValueFrom(this.concursoService.camposHabilitados())
    }

    const isUnidadeUsuarioDentroDasOpcoes = this.campusHabilitados.some(campus => campus.uuid === unidadeUsuario)

    this.uuid.setValue(isUnidadeUsuarioDentroDasOpcoes ? unidadeUsuario.toString() : null)
  }

  setIsTipoDocente(tipo: string | null): void {
    this.isTipoDocente = !(tipo == 'TECADM' || tipo == 'LD' || tipo == 'PESQ4' || tipo == 'PESQ3')

    if (!this.isTipoDocente) {
      this.numeroDespacho.setValidators([Validators.required, Validators.pattern(this.numeroDespachoPattern)])
      this.valorInscricao.setValidators(Validators.required)
      this.numeroDespacho.updateValueAndValidity()
      this.valorInscricao.updateValueAndValidity()
    }
  }

  applyTouchedToInvalidFields() {
    const arrNameErrors = []
    for (const name in this.form.controls) {
      if (this.form.get(name)?.invalid) {
        this.form.get(name)?.markAsTouched()
        arrNameErrors.push(name)
      }
    }
    if (this.editalUploadFc.invalid) {
      this.editalUploadFc.markAsTouched()
      arrNameErrors.push('upload')
    }
    return arrNameErrors
  }

  async processarFormulario() {
    const arrNameErros = this.applyTouchedToInvalidFields()

    if (this.hasPontos && !this.validaPontos()) return

    if (!this.form.valid || (this.ativo.value === 'true' && this.editalUploadFc.invalid)) {
      this.unespCoreMessageService.showMessageError(`Erro ao preencher os dados do formulário nos campos: ${arrNameErros.join(', ')}`)
      return
    }

    if (!this.comparaDatas()) {
      return
    }
    if (!this.comparaDatasSegundaFase()) {
      return
    }

    let formulario = <Concurso>this.form.value
    formulario.valorInscricao = formulario.valorInscricao != null ? formulario.valorInscricao.replace(',', '.') : ''
    formulario.salario = formulario.salario != null ? formulario.salario.replace(',', '.') : ''
    formulario.responsaveisRecurso = this.responsaveis.join(',')
    formulario.temas = this.pontosDataSource.data
    formulario.online = 'true'

    let isEdicao: boolean = !!this.id

    if (formulario.ativo === 'true') {
      this.cadastrarAtualizarConcurso(formulario, this.id)
    } else {
      const confirmDialog = new ConfirmDialogModel(
        'Atenção',
        `
        Você está ${
          isEdicao ? 'atualizando' : 'cadastrando'
        } um concurso que está com o campo "Ativo" marcado como "Não", isso implica que o concurso só ficará visível aos
        candidatos quando for alterado para "Sim". Deseja ${
          isEdicao ? 'atualizar' : 'cadastrar'
        } o concurso mesmo assim?`
      )
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: confirmDialog,
      })
      dialogRef.afterClosed().subscribe(res => {
        if (res) {
          this.cadastrarAtualizarConcurso(formulario, this.id)
        }
      })
    }

    const fimAlterado =
      this.dataFimOriginal && this.fim.value && this.dataFimOriginal.getTime() !== this.fim.value.getTime()
    const inicioAlterado =
      this.dataInicioOriginal && this.inicio.value && this.dataInicioOriginal.getTime() !== this.inicio.value.getTime()

    if (fimAlterado || inicioAlterado) {
      window.alert(
        'ATENÇÃO: O prazo das inscrições foi alterado. \n\nToda alteração no calendário do concurso que impacte os candidatos deve ser comunicado a estes. \n\nCrie um andamento do tipo Prorrogação das Inscrições. \n\nÉ necessário concluir o andamento para o envio de e-mail aos candidatos.'
      )
    }
  }

  private cadastrarAtualizarConcurso(formulario: Concurso, id?: number) {
    if (this.id) {
      this.concursoService.atualizar(this.id, formulario).subscribe(concurso => {
        this.editalUpload(concurso.id)
        this.alteracoesNosPontos = false
        this.form.markAsPristine()
        this.editalUploadFc.markAsPristine()
        this.unespCoreMessageService.showMessageSuccess(`Concurso #${concurso.id} atualizado com sucesso`)
        this.router.navigate(['/concurso'])
      })
    } else {
      this.concursoService.gravar(formulario).subscribe(
        concurso => {
          this.editalUpload(concurso.id)
          this.form.markAsPristine()
          this.editalUploadFc.markAsPristine()
          this.unespCoreMessageService.showMessageSuccess(`Concurso #${concurso.id} cadastrado com sucesso`)
          this.router.navigate(['/concurso'])
        },
        err => {
          const errorMsg = err?.error?.message || 'Erro ao cadastrar concurso'
          this.unespCoreMessageService.showMessageError(errorMsg)
        }
      )
    }
  }

  private editalUpload(id?: number | string) {
    if (typeof id == 'undefined') return

    this.upload!.uploadPath = 'edital/' + id
    if (this.editalUploadFc.value.status == ArquivoStatus.SELECIONADO) {
      this.upload?.upload()
    }
  }

  addChip(event: MatChipInputEvent): void {
    if (this.responsaveisRecurso.invalid) {
      this.responsaveisChipList!.errorState = true
      return
    }

    const value = (event.value || '').trim()
    if (value) {
      if (unicampEmailFormatValidator(value)) {
        this.responsaveis.push(value)
      } else {
        this.unespCoreMessageService.showMessageError(
          'Apenas emails do domínio UNICAMP são permitidos. Use o formato: seuemail@unicamp.br ou seuemail@siglaunidade.unicamp.br'
        )
      }
    }

    event.chipInput!.clear()
  }

  removeChip(resp: string): void {
    const index = this.responsaveis.indexOf(resp)
    if (index >= 0) this.responsaveis.splice(index, 1)
    this.form.markAsDirty()
  }

  setHasPontos(tipo: string | null): void {
    this.hasPontos = tipo == 'RDIDP' || tipo == 'RTP' || tipo == 'RTC'
  }

  validaPontos(): boolean {
    // Não é mais obrigatório
    // if (this.pontosDataSource.data.length == 0){
    //   this.unespCoreMessageService.showMessageError(`É nescessário cadastrar temas para ${this.tipo.value}`)
    // } else
    // if (this.editarPontoIdx != -1) {
    //   this.unespCoreMessageService.showMessageError("Complete ou cancele a edição do ponto")
    // } else if (this.pontoInput.touched) {
    //   this.unespCoreMessageService.showMessageError("O ponto não foi adicionado ainda")
    // } else {
    //   return true
    // }
    // this.pontoInput.markAsTouched()
    // this.pontoInput.setErrors({'incorrect': true})
    // return false
    return true
  }

  adicionarPonto() {
    let newTitulo = this.pontoInput.value ? this.pontoInput.value : ''
    if (newTitulo.length > 0) {
      let temas = this.pontosDataSource.data
      temas.push({
        data: new Date(),
        titulo: newTitulo,
        ativo: true,
      })
      this.pontosDataSource.data = temas
      this.limparPontoInput()
      this.alteracoesNosPontos = true
    } else {
      this.pontoInput.markAsTouched()
    }
  }
  atualizarPonto() {
    let newTitulo = this.pontoInput.value ? this.pontoInput.value : ''
    if (newTitulo.length > 0) {
      this.pontosDataSource.data[this.editarPontoIdx].titulo = newTitulo
      this.pontosDataSource.data[this.editarPontoIdx].data = new Date()
      this.limparPontoInput()
      this.alteracoesNosPontos = true
    }
  }

  editarPonto(tema: Tema, idx: number) {
    this.editarPontoIdx = idx
    this.pontoInput.setValue(tema.titulo)
  }

  limparPontoInput() {
    this.pontoInput.setValue('')
    this.pontoInput.markAsUntouched()
    this.editarPontoIdx = -1
  }

  excluirPonto(tema: Tema, idx: number) {
    if (this.editarPontoIdx != -1) this.limparPontoInput()
    this.pontosDataSource.data.splice(idx, 1)
    this.pontosDataSource.data = this.pontosDataSource.data
    this.alteracoesNosPontos = true
  }

  pontoInputOnBlur() {
    if (this.pontoInput.value == '' && this.editarPontoIdx == -1 && this.pontosDataSource.data.length > 0) {
      this.pontoInput.markAsUntouched()
    }
  }

  private formatarValor(valor: number): string {
    return valor.toFixed(2).replace('.', ',')
  }

  private parseLocalDate(value: string): Date {
    const [y, m, d] = value.split('-').map(Number)
    return new Date(y, m - 1, d)
  }

  hasPendingChanges(): boolean {
    return this.alteracoesNosPontos || this.form.dirty || this.editalUploadFc.dirty
  }
  pendingChangesMsg(): string {
    return 'Existem alterações não salvas. Deseja sair sem salvar?'
  }

  showTipoConcursoRH(): boolean {
    return this.unespCoreAuthService.hasPermissionBasedRoles(['RH'])
  }

  showTipoConcursoDTA(): boolean {
    return (
      !this.unespCoreAuthService.hasPermissionBasedRoles(['RH']) &&
      this.unespCoreAuthService.hasPermissionBasedRoles(['DTA'])
    )
  }

  clearDateInicioFase2(): void {
    this.inicioFase2.setValue(null)
  }

  clearDateFimFase2(): void {
    this.fimFase2.setValue(null)
  }

  comparaDatas(): boolean {
    if (this.fim.value != null && this.inicio.value != null) {
      if (this.fim.value < this.inicio.value) {
        this.unespCoreMessageService.showMessageError('Data de término da primeira fase menor que a data de início')
        return false
      } else {
        return true
      }
    } else {
      if (
        (this.fim.value != null && this.inicio.value == null) ||
        (this.fim.value == null && this.inicio.value != null)
      ) {
        this.unespCoreMessageService.showMessageError('Apenas uma das datas da primeira fase foi informada')
        return false
      } else {
        return true
      }
    }
  }

  comparaDatasSegundaFase(): boolean {
    if (this.fimFase2.value != null && this.inicioFase2.value != null) {
      if (this.fimFase2.value < this.inicioFase2.value) {
        this.unespCoreMessageService.showMessageError('Data de término da segunda fase menor que a data de início')
        return false
      } else {
        return true
      }
    } else {
      if (
        (this.fimFase2.value != null && this.inicioFase2.value == null) ||
        (this.fimFase2.value == null && this.inicioFase2.value != null)
      ) {
        this.unespCoreMessageService.showMessageError('Apenas uma das datas da segunda fase foi informada')
        return false
      } else {
        return true
      }
    }
  }

  horaDentroHorarioFuncionamentoUnicamp(control: AbstractControl): { [key: string]: boolean } | null {
    const hora = control.value
    if (hora) {
      const [hours, minutes] = hora.split(':').map(Number)
      const time = hours + minutes / 60
      if (time < 8 || time > 17) {
        return { horaInvalida: true }
      }
    }
    return null
  }

  private lastProcessoValidado = ''
  private validacaoSub?: Subscription

  validaProcesso(ev: any) {
    const processo = ev.target.value?.trim()
    if (!processo) {
      this.lastProcessoValidado = ''
      return
    }
    if (processo === this.lastProcessoValidado) return
    this.lastProcessoValidado = processo
    this.validacaoSub?.unsubscribe()
    this.validacaoSub = this.concursoService.validarProcessoConcurso({ processo, concursoId: this.id }).subscribe({
      next: () => this.processo.setErrors(null),
      error: () => {
        this.lastProcessoValidado = ''
        this.processo.setErrors({ processoInvalido: true })
      },
    })
  }

  excluirConcurso(): void {
    const confirmDialog = new ConfirmDialogModel('Atenção', `Confirma a exclusão do Concurso #${this.id}`)
    const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: confirmDialog })
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.concursoService.excluir(this.id!).subscribe(() => {
          this.unespCoreMessageService.showMessageSuccess(`Concurso #${this.id} excluído com sucesso`)
          this.router.navigate(['/concurso'])
        })
      }
    })
  }
}
